import React from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Plus, Trash2, AlertTriangle, Paintbrush, Car, Camera, Upload } from 'lucide-react';
import { getFromLocalStorage } from '../mock';
import { SEVERITY_OPTIONS, CONDITION_OPTIONS, WARNING_LIGHTS, DAMAGE_LOCATIONS } from './inspectionConstants';

const BACKEND_URL = ''; // same-origin: reverse proxy routes /api/* to backend

export const ConditionAssessment = ({ formData, setFormData }) => (
  <Card className="bg-gray-900 border-gray-700">
    <CardHeader className="pb-3"><CardTitle className="text-white text-base flex items-center gap-2"><Paintbrush className="h-4 w-4 text-blue-500" />Genel Durum Değerlendirmesi</CardTitle></CardHeader>
    <CardContent>
      <div className="grid grid-cols-3 gap-4">
        {[
          { key: 'paintCondition', label: 'Boya Durumu' },
          { key: 'interiorCondition', label: 'İç Mekan' },
          { key: 'tireCondition', label: 'Lastikler' },
        ].map(({ key, label }) => (
          <div key={key}>
            <Label className="text-gray-300 text-sm">{label}</Label>
            <Select value={formData[key]} onValueChange={v => setFormData(p => ({ ...p, [key]: v }))}>
              <SelectTrigger className="bg-gray-800 border-gray-700 text-white mt-1"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-gray-900 border-gray-700">
                {CONDITION_OPTIONS.map(o => <SelectItem key={o.value} value={o.value} className="text-white hover:bg-gray-800">{o.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        ))}
      </div>
    </CardContent>
  </Card>
);

export const ExteriorDamages = ({ formData, setFormData, newDamage, setNewDamage, onAddDamage, onRemoveDamage }) => (
  <Card className="bg-gray-900 border-gray-700">
    <CardHeader className="pb-3"><CardTitle className="text-white text-base flex items-center gap-2"><AlertTriangle className="h-4 w-4 text-brand" />Dış Hasar Kayıtları</CardTitle></CardHeader>
    <CardContent>
      <div className="space-y-3 mb-4">
        <div className="grid grid-cols-3 gap-2">
          <Select value={newDamage.location} onValueChange={v => setNewDamage(p => ({ ...p, location: v }))}>
            <SelectTrigger className="bg-gray-800 border-gray-700 text-white"><SelectValue placeholder="Konum" /></SelectTrigger>
            <SelectContent className="bg-gray-900 border-gray-700 max-h-48">
              {DAMAGE_LOCATIONS.map(l => <SelectItem key={l} value={l} className="text-white hover:bg-gray-800">{l}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={newDamage.severity} onValueChange={v => setNewDamage(p => ({ ...p, severity: v }))}>
            <SelectTrigger className="bg-gray-800 border-gray-700 text-white"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-gray-900 border-gray-700">
              {SEVERITY_OPTIONS.filter(o => o.value !== 'none').map(o => <SelectItem key={o.value} value={o.value} className="text-white hover:bg-gray-800">{o.label}</SelectItem>)}
            </SelectContent>
          </Select>
          <Input placeholder="Açıklama" value={newDamage.description} onChange={e => setNewDamage(p => ({ ...p, description: e.target.value }))}
            className="bg-gray-800 border-gray-700 text-white" />
        </div>
        <Button type="button" onClick={onAddDamage} size="sm" className="bg-brand hover:bg-brand text-white" data-testid="add-damage-btn">
          <Plus className="h-4 w-4 mr-1" /> Hasar Ekle
        </Button>
      </div>
      {formData.exteriorDamages.length > 0 ? (
        <div className="space-y-2">
          {formData.exteriorDamages.map((d, i) => {
            const sev = SEVERITY_OPTIONS.find(s => s.value === d.severity);
            return (
              <div key={`dmg-${d.location}-${d.severity}-${i}`} className="flex items-center justify-between bg-gray-800 p-3 rounded-lg">
                <div className="flex items-center gap-3">
                  <Badge className={`${sev?.color || 'bg-gray-500'} text-white`}>{sev?.label || d.severity}</Badge>
                  <span className="text-white font-medium">{d.location}</span>
                  {d.description && <span className="text-gray-400 text-sm">- {d.description}</span>}
                </div>
                <Button size="sm" variant="destructive" onClick={() => onRemoveDamage(i)}><Trash2 className="h-3 w-3" /></Button>
              </div>
            );
          })}
        </div>
      ) : <p className="text-gray-500 text-sm text-center py-2">Hasar kaydı yok</p>}
    </CardContent>
  </Card>
);

export const WarningLightsSection = ({ formData, toggleWarningLight }) => (
  <Card className="bg-gray-900 border-gray-700">
    <CardHeader className="pb-3"><CardTitle className="text-white text-base flex items-center gap-2"><Car className="h-4 w-4 text-red-500" />Uyarı Işıkları</CardTitle></CardHeader>
    <CardContent>
      <div className="flex flex-wrap gap-2">
        {WARNING_LIGHTS.map(light => (
          <Badge key={light}
            className={`cursor-pointer transition-all ${formData.warningLights.includes(light) ? 'bg-red-500 text-white' : 'bg-gray-800 text-gray-400 hover:bg-gray-700'}`}
            onClick={() => toggleWarningLight(light)}
            data-testid={`warning-light-${light.replace(/\s/g, '-')}`}
          >{light}</Badge>
        ))}
      </div>
    </CardContent>
  </Card>
);

export const PhotoUploadSection = ({ photos, uploading, onPhotoUpload, jobId }) => (
  <Card className="bg-gray-900 border-gray-700">
    <CardHeader className="pb-3"><CardTitle className="text-white text-base flex items-center gap-2"><Camera className="h-4 w-4 text-green-500" />Fotoğraflar</CardTitle></CardHeader>
    <CardContent>
      <div className="grid grid-cols-2 gap-4 mb-4">
        <div>
          <Label className="text-white text-sm mb-2 block">Hasar Fotoğrafları</Label>
          <label className="flex items-center justify-center gap-2 bg-gray-800 border border-dashed border-gray-600 rounded-lg p-4 cursor-pointer hover:border-brand transition-all">
            <Upload className="h-5 w-5 text-brand" />
            <span className="text-gray-300 text-sm">{uploading ? 'Yükleniyor...' : 'Hasar fotoğrafı seç'}</span>
            <input type="file" accept="image/*" multiple className="hidden" disabled={uploading}
              onChange={e => onPhotoUpload(e, 'inspection_damage')} data-testid="damage-photo-input" />
          </label>
        </div>
        <div>
          <Label className="text-white text-sm mb-2 block">Genel Fotoğraflar</Label>
          <label className="flex items-center justify-center gap-2 bg-gray-800 border border-dashed border-gray-600 rounded-lg p-4 cursor-pointer hover:border-blue-500 transition-all">
            <Upload className="h-5 w-5 text-blue-500" />
            <span className="text-gray-300 text-sm">{uploading ? 'Yükleniyor...' : 'Genel fotoğraf seç'}</span>
            <input type="file" accept="image/*" multiple className="hidden" disabled={uploading}
              onChange={e => onPhotoUpload(e, 'inspection_exterior')} data-testid="exterior-photo-input" />
          </label>
        </div>
      </div>
      {photos.length > 0 && (
        <div>
          <p className="text-gray-400 text-sm mb-2">{photos.length} fotoğraf yüklendi</p>
          <div className="grid grid-cols-4 gap-2">
            {photos.map(p => (
              <div key={p.id} className="relative group">
                <img src={`${BACKEND_URL}/api/photos/file/${p.id}?auth=${getFromLocalStorage('accessToken')}`}
                  alt={p.description} className="w-full h-24 object-cover rounded-lg border border-gray-700" />
                <Badge className="absolute top-1 left-1 text-xs bg-black/70">{p.category === 'inspection_damage' ? 'Hasar' : 'Genel'}</Badge>
              </div>
            ))}
          </div>
        </div>
      )}
    </CardContent>
  </Card>
);
